import React, { useState, useCallback } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import PlanRestoreAsync from '@atlassian/jira-portfolio-3-plan-archiving/src/async';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import messages from './messages';
import type { Props } from './types';

export default function ArchiveActionsCell(props: Props) {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const { plan, refetchPlans } = props;
	const [isPlanRestoreModalOpen, setIsPlanRestoreModalOpen] = useState<boolean>(false);
	const isAdmin = useIsAdmin();

	const openPlanRestoreModal = useCallback((): void => {
		setIsPlanRestoreModalOpen(true);
	}, []);

	const closePlanRestoreModal = useCallback((): void => {
		setIsPlanRestoreModalOpen(false);
	}, []);

	const onPlanRestoreError = useCallback((): void => {
		setIsPlanRestoreModalOpen(false);

		showFlag({
			type: 'error',
			title: messages.planRestoringFlagError,
		});
	}, [showFlag]);

	const onPlanRestoreComplete = useCallback((): void => {
		showFlag({
			type: 'success',
			title: messages.restoreSuccessMessageTitle,
		});
		setIsPlanRestoreModalOpen(false);
		refetchPlans();
	}, [refetchPlans, showFlag]);

	if (!isAdmin) {
		return null;
	}

	return (
		<div data-testid="inactive-plans-directory-base.ui.plans-table.body.cells.archive-actions-cell">
			<DropdownMenu
				placement="bottom-end"
				trigger={({ triggerRef, ...restProps }) => (
					<Button
						{...restProps}
						iconBefore={<MoreIcon label={formatMessage(messages.menuLabel)} />}
						ref={triggerRef}
						appearance="subtle"
					/>
				)}
			>
				<DropdownItemGroup>
					<DropdownItem onClick={openPlanRestoreModal}>
						{formatMessage(messages.actionRestorePlan)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
			{isPlanRestoreModalOpen && (
				<PlanRestoreAsync
					planId={plan.id}
					planTitle={plan.title || ''}
					onError={onPlanRestoreError}
					onComplete={onPlanRestoreComplete}
					onClose={closePlanRestoreModal}
					isArchived
				/>
			)}
		</div>
	);
}
